import React, { useState } from "react";
import { customerApi, apiError } from "@/lib/customerApi";

const TIERS = [["single", "Single bot", 49], ["five", "Five bots", 149], ["all", "Everything", 249]];
const gbp = (v) => `£${Number(v || 0).toFixed(2)}`;

function Quote({ q, terms, setTerms, busy, onConfirm, onCancel }) {
  return (
    <div data-testid="change-plan-quote" className="mt-4 rounded-xl border border-[#FBBF24]/50 bg-[#050914] p-5">
      <div className="text-lg font-medium">{q.upgrade ? "Upgrade" : "Downgrade"} to {q.tier_name}</div>
      {q.upgrade ? (
        <p className="text-sm text-[#CBD5E1] mt-2" data-testid="change-plan-due">
          You'll pay <b className="text-[#FBBF24]">{gbp(q.due_now)}</b> today - just the difference for the rest of this month{q.due_now === 0 ? " (covered by your account credit)" : ""}.
          After that it's {gbp(q.monthly_price)}/month from your next renewal. Your bots keep running.
        </p>
      ) : (
        <p className="text-sm text-[#CBD5E1] mt-2" data-testid="change-plan-credit">
          Switches today. The unused part of your current plan (<b className="text-[#34D399]">{gbp(q.credit)}</b>) comes off your
          next bill, so your next payment will be about <b className="text-[#FBBF24]">{gbp(q.next_bill)}</b>, then {gbp(q.monthly_price)}/month.
          If you have more bots picked than the new plan allows, only your first picks are kept.
        </p>
      )}
      <label className="flex items-start gap-2 mt-4 text-xs text-[#CBD5E1] cursor-pointer">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="change-plan-terms" className="mt-0.5 accent-[#FBBF24]" />
        I understand I'm buying a software licence, not investment advice or account management. Past and backtested results aren't reliable indicators of future performance and I can lose money.
      </label>
      <div className="flex gap-2 mt-4">
        <button disabled={!terms || busy} onClick={onConfirm} data-testid="change-plan-confirm"
          className="mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg bg-[#FBBF24] text-[#050914] disabled:opacity-40">
          {busy ? "Changing…" : q.upgrade ? `Pay ${gbp(q.due_now)} & upgrade` : "Confirm downgrade"}
        </button>
        <button onClick={onCancel} data-testid="change-plan-cancel" className="mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg border border-blue-900/60 text-[#94A3B8]">Cancel</button>
      </div>
    </div>
  );
}

export default function ChangePlan({ current }) {
  const [q, setQ] = useState(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const quote = async (tier) => {
    setErr(""); setQ(null); setTerms(false); setBusy(true);
    try { setQ((await customerApi.get("/store/account/change-plan/preview", { params: { tier } })).data); } catch (e) { setErr(apiError(e)); }
    setBusy(false);
  };
  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      await customerApi.post("/store/account/change-plan", { tier: q.tier, accepted_terms: terms });
      setDone(q); setQ(null);
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) { setErr(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="w-full" data-testid="change-plan">
      <div className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-2">Change plan - keeps your subscription and running bots</div>
      <div className="flex flex-wrap gap-2 mt-2">
        {TIERS.filter(([id]) => id !== current).map(([id, name, price]) => (
          <button key={id} onClick={() => quote(id)} disabled={busy} data-testid={`change-plan-${id}`}
            className="mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-lg border border-[#FBBF24]/60 text-[#FBBF24] hover:bg-[#FBBF24]/10 disabled:opacity-50">
            {name} · £{price}/mo
          </button>
        ))}
      </div>
      {q && <Quote q={q} terms={terms} setTerms={setTerms} busy={busy} onConfirm={confirm} onCancel={() => setQ(null)} />}
      {done && <div data-testid="change-plan-done" className="mt-3 text-sm text-[#34D399]">Done - you're now on {done.tier_name}. A confirmation email is on its way.</div>}
      {err && <div data-testid="change-plan-error" className="mt-3 text-xs text-[#F87171]">{err}</div>}
    </div>
  );
}
